import BaseRequest from './BaseRequest';
import { sanitizeApiDate, logSecurityEvent, buildSecureApiPath } from '../security/apiPathSecurity';
import { 
    secureAlertMarkRead, 
    secureAlertDelete, 
    secureCancelEmprestimo,
    secureCancelSolicitacao,
    secureGetEmprestimoDados,
    secureGetEmprestimoOcorrencias
} from '../security/secureHttpWrapper';

const SolicitacoesRequest = () => {
    return {
        gravarSolicitacaoComutacaoBibliografica(
            tipoSolicitacao,
            dadosFormulario,
        ) {
            // Build secure API path to prevent SSRF attacks
            const securePath = buildSecureApiPath('api/solicitacoes/comutacao-bibliografica', { tipoSolicitacao });
            if (!securePath) {
                logSecurityEvent('invalid_tipo_solicitacao', { providedTipo: String(tipoSolicitacao) });
                return Promise.reject(new Error('Invalid tipo solicitacao provided'));
            }

            return BaseRequest.axiosInst.post(
                securePath,
                dadosFormulario,
                { headers: BaseRequest.privateHeaders() },
            );
        },
        buscarSolicitacoesUsuarioPorTipo(tipoSolicitacao, status) {
            // Build secure API path to prevent SSRF attacks
            const securePath = buildSecureApiPath('api/solicitacoes', { tipoSolicitacao });
            if (!securePath) {
                logSecurityEvent('invalid_tipo_solicitacao_busca', { providedTipo: String(tipoSolicitacao) });
                return Promise.reject(new Error('Invalid tipo solicitacao provided'));
            }

            return BaseRequest.axiosInst.get(
                securePath,
                { headers: BaseRequest.privateHeaders(), params: { status } },
            );
        },
        gravarSolicitacaoLevantamentoBibliografico(dadosFormulario) {
            return BaseRequest.axiosInst.post(
                'api/solicitacoes/levantamento-bibliografico',
                dadosFormulario,
                { headers: BaseRequest.privateHeaders() },
            );
        },
        gravarSolicitacaoFichaCatalografica(dadosFormulario) {
            return BaseRequest.axiosInst.post(
                'api/solicitacoes/ficha-catalografica',
                dadosFormulario,
                { headers: BaseRequest.privateHeaders() },
            );
        },
        gravarSolicitacaoPesquisaEspecializada(dadosFormulario) {
            return BaseRequest.axiosInst.post(
                'api/solicitacoes/pesquisa-especializada',
                dadosFormulario,
                { headers: BaseRequest.privateHeaders() },
            );
        },
        buscarAlertasSolicitacoesUsuario(dataIni, dataFim) {
            // Sanitize date parameters to prevent injection attacks
            const sanitizedDataIni = sanitizeApiDate(dataIni);
            const sanitizedDataFim = sanitizeApiDate(dataFim);
            
            // Validate required date parameters
            if ((dataIni && !sanitizedDataIni) || (dataFim && !sanitizedDataFim)) {
                logSecurityEvent('invalid_date_parameters', { 
                    dataIni: String(dataIni), 
                    dataFim: String(dataFim) 
                });
                return Promise.reject(new Error('Invalid date parameters provided'));
            }

            const params = {};
            if (sanitizedDataFim) params.dataFim = sanitizedDataFim;
            if (sanitizedDataIni) params.dataIni = sanitizedDataIni;

            return BaseRequest.axiosInst.get('api/solicitacoes/alertas', {
                headers: BaseRequest.privateHeaders(),
                params,
            });
        },
        marcarLidoAlertaSolicitacao(idAlerta) {
            // Use secure wrapper to prevent SSRF attacks
            return secureAlertMarkRead(idAlerta);
        },
        excluirAlertaSolicitacaoUsuario(idAlerta) {
            // Use secure wrapper to prevent SSRF attacks
            return secureAlertDelete(idAlerta);
        },
        buscarDadosEmprestimoInterBibliotecas() {
            // Use secure wrapper to prevent SSRF attacks
            return secureGetEmprestimoDados();
        },
        gravarEmprestimoInterBibliotecas(dadosFormulario) {
            return BaseRequest.axiosInst.post(
                'api/solicitacoes/emprestimo-inter-bibliotecas',
                dadosFormulario,
                { headers: BaseRequest.privateHeaders() },
            );
        },
        getOcorrenciasSolicitacoesEmprestimo() {
            // Use secure wrapper to prevent SSRF attacks
            return secureGetEmprestimoOcorrencias();
        },
        cancelSolicitacaoEmprestimo(id) {
            // Use secure wrapper to prevent SSRF attacks
            return secureCancelEmprestimo(id);
        },
        cancelSolicitacao(data) {
            // Validate that data is provided and is an object
            if (!data || typeof data !== 'object') {
                logSecurityEvent('invalid_cancel_data', { providedData: String(data) });
                return Promise.reject(new Error('Invalid cancellation data provided'));
            }

            // Create a sanitized copy of the data to prevent prototype pollution
            const sanitizedData = {};
            
            // Only allow specific known properties for cancellation
            const allowedFields = ['id', 'motivo', 'tipo', 'observacoes', 'codServico'];
            
            for (const field of allowedFields) {
                if (data[field] !== undefined) {
                    // Basic sanitization for each field
                    if (typeof data[field] === 'string') {
                        // Remove dangerous characters and limit length
                        const sanitizedValue = String(data[field])
                            .trim()
                            .substring(0, 500)
                            .replace(/[<>'"&]/g, ''); // Remove basic XSS chars
                        
                        if (sanitizedValue) {
                            sanitizedData[field] = sanitizedValue;
                        }
                    } else if (typeof data[field] === 'number' && (field === 'id' || field === 'codServico')) {
                        sanitizedData[field] = data[field];
                    }
                }
            }

            // Use secure wrapper to prevent SSRF attacks
            return secureCancelSolicitacao(sanitizedData);
        },
    };
};

export default SolicitacoesRequest();
